import { ReactElement } from 'react';

import { AppIcon, AcademicEnrollmentType } from '@backrs_ui/common-ui';

import { ProfileFields } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, Divider, Theme, SxProps, Typography, Chip } from '@mui/material';

import { ChecklistItemStatus } from './ChecklistItemStatus';

type ChecklistItemProps = Readonly<{
  id?: string;
  label: string;
  isComplete: boolean;
  description?: string;
  fieldKey: ProfileFields | ProfileFields[] | null;
  editComponent?: ReactElement;
  enrollmentType?: AcademicEnrollmentType;
  disableEditing: boolean;
  optional?: boolean;
  customOnEdit?: (navigate: (path: string) => void, doesUserHaveBlueprint: boolean) => void;
  onClick: (
    field: ProfileFields | ProfileFields[] | null,
    customOnEdit?: (navigate: (path: string) => void, doesUserHaveBlueprint: boolean) => void,
  ) => void;
}>;

export const ChecklistItem = ({
  id,
  label,
  isComplete,
  description,
  fieldKey,
  onClick,
  disableEditing,
  optional,
  customOnEdit,
}: ChecklistItemProps) => {
  return (
    <Box
      id={id}
      sx={itemContainerSx(disableEditing)}
      onClick={() => (disableEditing ? undefined : onClick(fieldKey, customOnEdit))}
    >
      <Box sx={statusContainerSx}>
        <ChecklistItemStatus isCompleted={isComplete} isRequired={!optional} />
      </Box>
      <Box sx={itemContentSx}>
        <Box sx={titleRowSx}>
          <Box sx={titleContentSx}>
            <Typography variant="text-sm-medium">{label}</Typography>
            {description && (
              <Typography
                variant="text-sm-regular"
                color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
              >
                {description}
              </Typography>
            )}
          </Box>
          <Box sx={actionButtonsSx}>
            {!isComplete && !optional && (
              <Chip
                label="Required"
                variant="outlined"
                icon={
                  <AppIcon iconName="Circle" diameter="8px" color="Error/500" fill="Error/500" />
                }
                sx={requiredPill}
              />
            )}
            {!disableEditing && (
              <Box sx={editButtonSx}>
                <AppIcon iconName="Edit01" diameter="16px" color="Gray/400" />
              </Box>
            )}
          </Box>
        </Box>
        <Divider sx={dividerSx} />
      </Box>
    </Box>
  );
};

const itemContainerSx =
  (disableEditing: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: backrs.spacing['spacing-sm'],
    borderRadius: backrs.radius['radius-md'],
    gap: backrs.spacing['spacing-xl'],
    cursor: disableEditing ? 'unset' : 'pointer',
  });

const itemContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  flex: 1,
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-sm'],
  width: '100%',
});

const statusContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: backrs.spacing['spacing-md'], // extra spacing for divider
});

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  width: '100%',
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
});

const titleContentSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const actionButtonsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  '@container (min-width: 600px)': {
    gap: backrs.spacing['spacing-xl'],
    marginRight: backrs.spacing['spacing-xl'],
  },
});

const editButtonSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const requiredPill: SxProps<Theme> = ({ backrs, typography }) => ({
  border: `1px solid ${backrs.colors['Gray/300']}`,
  borderRadius: backrs.radius['radius-sm'],
  ...typography['text-xs-medium'],
  height: 'auto',
  paddingLeft: backrs.spacing['spacing-sm'],
  paddingRight: backrs.spacing['spacing-sm'],
  paddingTop: '1px',
  paddingBottom: '1px',
  '& .MuiChip-icon': {
    marginLeft: 0,
    marginRight: 0,
  },
  '& .MuiChip-label': {
    paddingLeft: backrs.spacing['spacing-sm'],
    paddingRight: 0,
  },
});
